import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Printer, Download, ArrowLeft, Building2, MapPin, Phone, Mail, Landmark, Info, User, Package } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import { findState } from './indianStates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
// Product GST is stored as a percent (5, 18) or, for imported products, a fraction (0.05, 0.18).
const normRate = (v) => { const r = Number(v) || 0; return r > 0 && r < 1 ? round2(r * 100) : r; };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '');
const invoiceNo = (prefix, orderNumber) => (orderNumber ? `${prefix}${orderNumber.replace('ORD-', '')}` : '');

export default function InvoicePrint() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('it_token');

  useEffect(() => {
    const load = async () => {
      try {
        const [oRes, sRes] = await Promise.all([
          axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/settings`),
        ]);
        const found = oRes.data.find((o) => o.id === orderId || o.order_number === orderId);
        if (!found) { setError('Order not found'); return; }
        setOrder(found);
        setSeller(sRes.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load order');
      } finally { setLoading(false); }
    };
    load();
  }, [orderId, token]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className='flex items-center justify-center h-screen bg-slate-50'>
      <div className='w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin' />
    </div>
  );

  if (error) return (
    <div className='flex flex-col items-center justify-center h-screen bg-slate-50 gap-4'>
      <p className='text-red-500 font-semibold'>{error}</p>
      <Link to='/admin/orders' className='text-blue-600 text-sm hover:underline flex items-center gap-1'>
        <ArrowLeft className='w-4 h-4' /> Back to Orders
      </Link>
    </div>
  );

  // Every show/hide toggle defaults to on until the owner turns it off in Settings.
  const show = (key) => seller?.[key] !== false;

  const deliveryCharge = Number(order.delivery_charge || 0);
  const couponDiscount = Number(order.coupon_discount || 0);
  const paymentSurcharge = Number(order.payment_surcharge || 0);
  const total = Number(order.total || 0);
  const gst = order.gst_details || {};
  const isBusiness = !!(gst.gstin || gst.business_name);
  const sellerName = seller?.seller_name || 'IMPORTREND';
  const sellerGstin = seller?.seller_gstin || '';
  const sellerAddress = [seller?.seller_address, seller?.seller_city, seller?.seller_state, seller?.seller_pincode].filter(Boolean).join(', ');
  const invoiceNumber = invoiceNo(seller?.invoice_prefix ?? 'INV-', order.order_number);

  // Prices are GST-inclusive, so tax is backed out of each line: taxable = gross / (1 + rate).
  const rows = (order.items || []).map((item) => {
    const qty = Number(item.quantity || 1);
    const unitGross = Number(item.price ?? item.unit_price ?? 0);
    const gross = Number(item.amount ?? item.total_price ?? unitGross * qty);
    const own = normRate(item.tax_rate ?? item.gst_rate ?? item.gst);
    const rate = own > 0 ? own : normRate(seller?.invoice_default_tax_rate);
    const taxable = round2(gross / (1 + rate / 100));
    return { item, qty, gross, rate, taxable, tax: round2(gross - taxable), unit: round2(taxable / qty), hsn: item.hsn_code || item.hsn || '' };
  });
  const taxableTotal = round2(rows.reduce((a, r) => a + r.taxable, 0));
  const taxTotal = round2(rows.reduce((a, r) => a + r.tax, 0));
  const taxByRate = {};
  rows.forEach((r) => { taxByRate[r.rate] = round2((taxByRate[r.rate] || 0) + r.tax); });
  const rates = Object.keys(taxByRate).map(Number).sort((a, b) => a - b);

  // Place of supply comes from the delivery address; intra/inter-state needs the seller state too.
  const buyerState = findState(order.delivery_address);
  const sellerState = findState(seller?.seller_state);
  const supply = buyerState && sellerState ? (buyerState.code === sellerState.code ? 'intra' : 'inter') : 'unknown';
  const supplyLabel = { intra: 'Intra-State (Within State)', inter: 'Inter-State', unknown: '-' }[supply];
  const applicableGst = rates.length === 1
    ? (supply === 'intra' ? `${rates[0]}% (CGST ${rates[0] / 2}% + SGST ${rates[0] / 2}%)`
      : supply === 'inter' ? `${rates[0]}% (IGST ${rates[0]}%)` : `${rates[0]}%`)
    : rates.length > 1 ? 'Multiple rates' : '-';

  const showHsn = show('invoice_show_hsn');
  const showTaxCols = show('invoice_show_tax_columns');
  const th = 'px-3 py-2.5 text-[11px] font-black text-slate-500 uppercase tracking-wider';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          .invoice-wrapper { box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
        }
        @page { margin: 15mm; size: A4; }
      `}</style>

      <div className='no-print bg-slate-800 text-white px-6 py-3 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Link to='/admin/orders' className='text-slate-300 hover:text-white flex items-center gap-1.5 text-sm transition-colors'>
            <ArrowLeft className='w-4 h-4' /> Back to Orders
          </Link>
          <span className='text-slate-600'>|</span>
          <span className='text-sm font-semibold'>{order.order_number}</span>
        </div>
        <div className='flex items-center gap-2'>
          <Link to='/admin/settings' className='text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            Invoice settings
          </Link>
          <Link to={`/admin/shipping-label/${order.id}`} data-testid='invoice-shipping-label-link' className='text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            Shipping label
          </Link>
          <a
            href={`${API}/admin/orders/export-csv?status=`}
            download='orders.csv'
            className='flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'
          >
            <Download className='w-3.5 h-3.5' /> Download CSV
          </a>
          <button
            onClick={handlePrint}
            data-testid='invoice-print-btn'
            className='flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-1.5 font-bold transition-colors'
          >
            <Printer className='w-3.5 h-3.5' /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className='bg-slate-100 min-h-screen py-8 px-4'>
        <div className='invoice-wrapper max-w-[794px] mx-auto bg-white shadow-lg rounded-lg overflow-hidden'>

          <div className='bg-slate-900 px-8 py-5 flex items-start justify-between'>
            <div>
              <h1 className='text-2xl font-black text-white tracking-tight'>
                <BrandLogo size='lg' textVariant='light' />
              </h1>
              {show('invoice_show_seller_gstin') && sellerGstin && (
                <p className='text-slate-400 text-xs mt-1 font-mono' data-testid='invoice-seller-gstin'>GSTIN: {sellerGstin}</p>
              )}
              {show('invoice_show_seller_address') && sellerAddress && (
                <p className='text-slate-400 text-xs mt-0.5'>{sellerAddress}</p>
              )}
              {seller?.seller_email && <p className='text-slate-400 text-xs'>{seller.seller_email}</p>}
            </div>
            <div className='text-right'>
              <div className={`inline-block px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase mb-2 ${isBusiness ? 'bg-amber-400 text-slate-900' : 'bg-orange-500 text-white'}`}>
                {isBusiness ? 'Tax Invoice (B2B)' : 'Invoice'}
              </div>
              <p className='text-white text-lg font-black'>{invoiceNumber}</p>
              <p className='text-slate-400 text-xs mt-0.5'>Order: {order.order_number}</p>
              <p className='text-slate-400 text-xs'>Date: {fmtDate(order.created_at)}</p>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-0 border-b border-slate-200'>
            <div className='px-8 py-5 border-r border-slate-200'>
              <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5'>
                <User className='w-3.5 h-3.5' /> {isBusiness ? 'Bill To (Business)' : 'Bill To'}
              </p>
              {isBusiness && gst.business_name && (
                <p className='text-sm font-black text-slate-900 flex items-center gap-1.5 mb-1'>
                  <Building2 className='w-3.5 h-3.5 text-amber-500 flex-shrink-0' />
                  {gst.business_name}
                </p>
              )}
              <p className='text-sm font-semibold text-slate-800'>{order.customer_name}</p>
              {order.customer_phone && (
                <p className='text-xs text-slate-500 flex items-center gap-1 mt-1'><Phone className='w-3 h-3' /> {order.customer_phone}</p>
              )}
              {order.customer_email && !order.customer_email.includes('.local') && (
                <p className='text-xs text-slate-500 flex items-center gap-1'><Mail className='w-3 h-3' /> {order.customer_email}</p>
              )}
            </div>

            <div className='px-8 py-5'>
              <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5'>
                <MapPin className='w-3.5 h-3.5' /> Ship To
              </p>
              <p className='text-sm font-semibold text-slate-800'>{order.customer_name}</p>
              {order.delivery_address && (
                <p className='text-xs text-slate-500 mt-1 leading-relaxed'>{order.delivery_address}</p>
              )}
              {show('invoice_show_payment') && (
                <div className='mt-2 flex items-center gap-2'>
                  <span className='text-[10px] text-slate-400 font-semibold uppercase'>Payment</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {order.payment_method || 'COD'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className='px-8 py-5'>
            <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5'>
              <Package className='w-3.5 h-3.5' /> Items
            </p>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='bg-slate-50 border border-slate-200'>
                  <th className={`text-left w-8 ${th}`}>#</th>
                  <th className={`text-left ${th}`}>Description</th>
                  {show('invoice_show_sku') && <th className={`text-left w-20 ${th}`}>SKU</th>}
                  <th className={`text-center w-12 ${th}`}>Qty</th>
                  <th className={`text-right w-24 ${th}`}>{showTaxCols ? 'Unit Price' : 'Rate'}</th>
                  {showTaxCols && <th className={`text-right w-14 ${th}`}>Tax %</th>}
                  {showTaxCols && <th className={`text-right w-24 ${th}`}>Tax Amt</th>}
                  <th className={`text-right w-24 ${th}`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className='border-b border-slate-100'>
                    <td className='px-3 py-3 text-xs text-slate-500'>{i + 1}</td>
                    <td className='px-3 py-3'>
                      <p className='text-sm font-semibold text-slate-800 leading-snug'>{r.item.product_name || r.item.name}</p>
                      {r.item.variant && <p className='text-xs text-slate-400 mt-0.5'>{r.item.variant}</p>}
                      {showHsn && r.hsn && <p className='text-[11px] text-slate-400 mt-0.5'>HSN: {r.hsn}</p>}
                    </td>
                    {show('invoice_show_sku') && <td className='px-3 py-3 text-[11px] font-mono text-slate-500'>{r.item.sku || '-'}</td>}
                    <td className='px-3 py-3 text-xs text-center font-semibold text-slate-700'>{r.qty}</td>
                    <td className='px-3 py-3 text-xs text-right font-semibold text-slate-700'>{showTaxCols ? `₹${money(r.unit)}` : `₹${money(r.gross / r.qty)}`}</td>
                    {showTaxCols && <td className='px-3 py-3 text-xs text-right text-slate-600'>{r.rate}%</td>}
                    {showTaxCols && <td className='px-3 py-3 text-xs text-right text-slate-600'>₹{money(r.tax)}</td>}
                    <td className='px-3 py-3 text-xs text-right font-bold text-slate-900'>₹{money(r.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='px-8 pb-5 grid grid-cols-2 gap-6 items-start'>
            {show('invoice_show_gst_details') ? (
              <div className='border border-slate-200 rounded-xl overflow-hidden' data-testid='invoice-gst-details'>
                <p className='bg-slate-50 px-4 py-2.5 text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5'>
                  <Landmark className='w-3.5 h-3.5' /> GST Details
                </p>
                <dl className='text-xs divide-y divide-slate-100'>
                  {show('invoice_show_seller_gstin') && (
                    <div className='flex px-4 py-2'><dt className='w-32 text-slate-500'>Seller GSTIN</dt><dd className='font-mono font-semibold text-slate-800'>{sellerGstin || '-'}</dd></div>
                  )}
                  <div className='flex px-4 py-2'><dt className='w-32 text-slate-500'>Buyer GSTIN</dt><dd className='font-mono font-semibold text-slate-800'>{(isBusiness && gst.gstin) || '-'}</dd></div>
                  <div className='flex px-4 py-2'><dt className='w-32 text-slate-500'>Place of Supply</dt><dd className='font-semibold text-slate-800'>{buyerState ? `${buyerState.name} (${buyerState.code})` : '-'}</dd></div>
                  <div className='flex px-4 py-2'><dt className='w-32 text-slate-500'>Supply Type</dt><dd className='font-semibold text-slate-800'>{supplyLabel}</dd></div>
                  <div className='flex px-4 py-2'><dt className='w-32 text-slate-500'>Applicable GST</dt><dd className='font-semibold text-slate-800'>{applicableGst}</dd></div>
                </dl>
              </div>
            ) : <div />}

            <div className='space-y-1.5 text-xs text-slate-600'>
              <div className='flex justify-between'><span>Subtotal (Before Tax)</span><span className='font-semibold'>₹{money(taxableTotal)}</span></div>
              <div className='flex justify-between'><span>Total Tax</span><span className='font-semibold'>₹{money(taxTotal)}</span></div>
              {show('invoice_show_tax_breakup') && supply !== 'unknown' && rates.map((rate) => (
                <div key={rate} className='pl-3 space-y-0.5 text-slate-500'>
                  {supply === 'intra' ? (
                    <>
                      <div className='flex justify-between'><span>CGST @ {rate / 2}%</span><span>₹{money(round2(taxByRate[rate] / 2))}</span></div>
                      <div className='flex justify-between'><span>SGST @ {rate / 2}%</span><span>₹{money(round2(taxByRate[rate] - round2(taxByRate[rate] / 2)))}</span></div>
                    </>
                  ) : (
                    <div className='flex justify-between'><span>IGST @ {rate}%</span><span>₹{money(taxByRate[rate])}</span></div>
                  )}
                </div>
              ))}
              {couponDiscount > 0 && (
                <div className='flex justify-between text-green-600'>
                  <span>Discount {order.coupon_code ? `(${order.coupon_code})` : ''}</span>
                  <span className='font-semibold'>-₹{money(couponDiscount)}</span>
                </div>
              )}
              {deliveryCharge > 0 && <div className='flex justify-between'><span>Shipping Charges</span><span className='font-semibold'>₹{money(deliveryCharge)}</span></div>}
              {paymentSurcharge > 0 && <div className='flex justify-between'><span>Payment Surcharge</span><span className='font-semibold'>₹{money(paymentSurcharge)}</span></div>}
              <div className='flex justify-between items-center text-sm font-black text-slate-900 pt-2 border-t border-slate-200'>
                <span>Total Amount (Incl. of GST)</span><span className='bg-orange-500 text-white rounded-lg px-3 py-1'>₹{money(total)}</span>
              </div>
            </div>
          </div>

          <div className='px-8 pb-5'>
            <div className='bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-start gap-3'>
              <Info className='w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5' />
              <div className='text-xs text-slate-500'>
                <p><span className='font-black text-slate-700 uppercase tracking-wider mr-2'>Tax Information</span>{seller?.invoice_tax_note ?? 'All prices are inclusive of applicable GST.'}</p>
                {order.notes && <p className='mt-1'><span className='font-semibold'>Note:</span> {order.notes}</p>}
              </div>
            </div>
          </div>

          <div className='bg-slate-900 px-8 py-4 flex items-center justify-between'>
            <p className='text-xs text-slate-400'>{seller?.invoice_footer_note ?? 'This is a computer-generated invoice and does not require a signature.'}</p>
            <p className='text-xs text-slate-500 font-semibold'>{sellerName}</p>
          </div>
        </div>
      </div>
    </>
  );
}
